import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/shared/lib/rbac";
import { myBillingService, type MyBalanceView } from "../api/myBillingService";
import { creditPoolLowBalance } from "../lib/lowBalance";

/**
 * 站内低余额提示(0926 修复波 F)。积分池剩余 < 5% 时出现:「积分快用完了(剩 N),请联系我们充值」。
 *
 * ★只做站内提示,不做企微推送(拍板)。此前「我的套餐」文案写「低于 5% 会触发系统预警」,
 *   但那条告警只发平台 webhook 且默认关 —— 租户以为会收到,实际谁也收不到。
 */
export function LowBalanceBanner({
  balances,
  showPlanLink = false,
}: {
  balances: readonly MyBalanceView[] | null | undefined;
  showPlanLink?: boolean;
}) {
  const { t } = useTranslation("billing");
  const navigate = useNavigate();
  const low = creditPoolLowBalance(balances);
  if (!low) return null;
  return (
    <div
      role="alert"
      data-testid="low-balance-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-foreground"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
      <span className="min-w-0 flex-1">
        {t("lowBalance.message", { remaining: low.remaining.toLocaleString("zh-CN") })}
      </span>
      {showPlanLink && (
        <button
          type="button"
          onClick={() => navigate("/my-billing")}
          className="whitespace-nowrap text-xs font-medium text-primary hover:underline"
        >
          {t("lowBalance.viewPlan")}
        </button>
      )}
    </div>
  );
}

/**
 * 工作台首页用:自己拉余额。只有租户超管读得到 /tenant/billing/me(其余角色后端 403),
 * 所以其它角色直接不查、不显示。
 */
export function TenantLowBalanceBanner() {
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === ROLES.TENANT_ADMIN;
  const { data } = useQuery({
    queryKey: ["my", "balance"],
    queryFn: () => myBillingService.listBalance(),
    enabled: isAdmin,
    retry: false,
  });
  if (!isAdmin) return null;
  return <LowBalanceBanner balances={data} showPlanLink />;
}
